/**
 * "Ver grabación" (F3-G): botón que abre un diálogo con el player VOD estándar
 * (F3-F `VideoPlayer`) reproduciendo la grabación enlazada a la sesión. Es el
 * requisito clave — los alumnos re-ven la clase con el mismo player (reanudación
 * local-first incluida). Reutilizable en tarjetas de sesión y en el módulo
 * `live_session`.
 */
import { type Component, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { PlayCircle } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { VideoPlayer } from "@/features/media/components/video-player";

export interface RecordingPlayerProps {
  /** `class_session.recording_video_id` (el video_asset de la grabación). */
  videoId: string;
  /** Título accesible (p.ej. el tema de la sesión). */
  title?: string;
  /** Estilo del botón disparador (heredable). */
  triggerClass?: string;
}

export const RecordingPlayer: Component<RecordingPlayerProps> = (props) => {
  const { t } = useI18n();
  const [open, setOpen] = createSignal(false);
  return (
    <>
      <button
        type="button"
        data-testid="watch-recording"
        onClick={() => setOpen(true)}
        class={
          props.triggerClass ??
          "inline-flex items-center gap-1.5 rounded-lg bg-accent/12 px-3 py-1.5 text-xs font-bold text-accent transition-colors hover:bg-accent/20"
        }
      >
        <PlayCircle class="h-4 w-4" />
        {t(msg`Watch recording`)}
      </button>
      <Show when={open()}>
        <Dialog open={open()} onOpenChange={setOpen} contentClass="w-full max-w-3xl p-4">
          <div class="space-y-3" data-testid="recording-dialog">
            <h2 class="text-sm font-bold">{props.title ?? t(msg`Class recording`)}</h2>
            <VideoPlayer videoId={props.videoId} title={props.title} />
          </div>
        </Dialog>
      </Show>
    </>
  );
};
